import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Tag, Plus, Trash2, Lock, Pencil } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState, TableSkeleton } from "@/components/EmptyState";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FieldsManager } from "@/components/FieldsManager";
import { UnsplashPicker } from "@/components/UnsplashPicker";
import { api } from "@/lib/api";

const EMPTY_FORM = { name: "", description: "", image: "", is_premium: false };

function TypeForm({ form, setForm }) {
  return (
    <div className="space-y-4 py-4">
      <div className="space-y-1.5">
        <Label htmlFor="t-name">Name</Label>
        <Input
          id="t-name"
          data-testid="type-name-input"
          required
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="t-desc">Description</Label>
        <Textarea
          id="t-desc"
          data-testid="type-desc-input"
          rows={3}
          value={form.description || ""}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
      </div>
      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <Label htmlFor="t-image">Cover image URL</Label>
          <UnsplashPicker onSelect={(url) => setForm({ ...form, image: url })} />
        </div>
        <Input
          id="t-image"
          data-testid="type-image-input"
          placeholder="https://images.unsplash.com/photo-…   or paste any direct image URL"
          value={form.image || ""}
          onChange={(e) => setForm({ ...form, image: e.target.value })}
        />
        {form.image && (
          <div className="mt-1 rounded-md border border-border overflow-hidden bg-muted aspect-[16/9] max-w-xs">
            <img
              src={form.image}
              alt="Cover preview"
              className="size-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          </div>
        )}
      </div>
      <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/30 p-3">
        <div className="size-9 rounded-lg bg-accent/15 text-accent flex items-center justify-center shrink-0">
          <Lock className="size-[18px]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <Label htmlFor="t-premium" className="text-sm font-medium cursor-pointer">
              Premium type
            </Label>
            <Switch
              id="t-premium"
              data-testid="type-premium-toggle"
              checked={!!form.is_premium}
              onCheckedChange={(v) => setForm({ ...form, is_premium: !!v })}
            />
          </div>
          <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
            Devotionals in this type can only be read by users with an active subscription.
            Free users see the title and thumbnail but are prompted to upgrade.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function DevotionalTypes() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const reload = () =>
    api.get("/devotional-types").then((r) => setItems(r.data)).finally(() => setLoading(false));

  useEffect(() => {
    reload();
  }, []);

  const onCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data: created } = await api.post("/devotional-types", form);
      toast.success(`"${created.name}" created — now configure its fields`);
      setCreateOpen(false);
      setForm(EMPTY_FORM);
      await reload();
      // Open the edit dialog scrolled to the fields section so admin can
      // immediately configure the structure of devotionals in this type.
      openEdit(created);
    } catch (err) {
      toast.error(err.userMessage);
    } finally {
      setSaving(false);
    }
  };

  const onSaveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/devotional-types/${editTarget.id}`, form);
      toast.success("Devotional type updated");
      setEditTarget(null);
      setForm(EMPTY_FORM);
      reload();
    } catch (err) {
      toast.error(err.userMessage);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/devotional-types/${deleteTarget.id}`);
      toast.success(`Deleted "${deleteTarget.name}"`);
      reload();
    } catch (err) {
      toast.error(err.userMessage);
    } finally {
      setDeleteTarget(null);
    }
  };

  const openEdit = (type) => {
    setForm({
      name: type.name,
      description: type.description || "",
      image: type.image || "",
      is_premium: !!type.is_premium,
    });
    setEditTarget(type);
  };

  return (
    <>
      <PageHeader
        title="Devotional Types"
        description="Categories of devotional content. Mark a type as Premium to require a subscription for it."
        actions={
          <Dialog
            open={createOpen}
            onOpenChange={(v) => {
              setCreateOpen(v);
              if (!v) setForm(EMPTY_FORM);
            }}
          >
            <DialogTrigger asChild>
              <Button data-testid="add-type-button">
                <Plus className="size-4 mr-1.5" /> New Type
              </Button>
            </DialogTrigger>
            <DialogContent>
              <form onSubmit={onCreate}>
                <DialogHeader>
                  <DialogTitle>Create devotional type</DialogTitle>
                  <DialogDescription>
                    A new content category. After creating, you'll be taken straight to its
                    fields configuration — that's where you define what each devotional in this
                    type will contain (Topic, Scripture, Questions, etc.).
                  </DialogDescription>
                </DialogHeader>
                <TypeForm form={form} setForm={setForm} />
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={saving} data-testid="type-submit-button">
                    {saving ? "Saving…" : "Create"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      {loading ? (
        <Card className="p-4">
          <TableSkeleton rows={4} cols={3} />
        </Card>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Tag}
          title="No devotional types yet"
          description="Create your first type to start organizing content."
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((t) => (
            <Card
              key={t.id}
              data-testid={`type-card-${t.id}`}
              className="overflow-hidden border-border/70 shadow-sm hover:shadow-md transition-shadow group relative"
            >
              {t.is_premium && (
                <div className="absolute top-3 left-3 z-10">
                  <Badge className="bg-accent text-accent-foreground border-transparent flex items-center gap-1">
                    <Lock className="size-3" /> Premium
                  </Badge>
                </div>
              )}
              {t.image && (
                <div className="aspect-[16/9] bg-muted overflow-hidden">
                  <img
                    src={t.image}
                    alt={t.name}
                    className="size-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
                  />
                </div>
              )}
              <div className="p-4">
                <h3 className="font-semibold tracking-tight">{t.name}</h3>
                <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{t.description || "—"}</p>
                <div className="mt-4 flex justify-between items-center gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => openEdit(t)}
                    data-testid={`type-edit-${t.id}`}
                  >
                    <Pencil className="size-3.5 mr-1.5" /> Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={() => setDeleteTarget(t)}
                    data-testid={`type-delete-${t.id}`}
                  >
                    <Trash2 className="size-3.5 mr-1.5" /> Delete
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Edit dialog */}
      <Dialog
        open={!!editTarget}
        onOpenChange={(v) => {
          if (!v) {
            setEditTarget(null);
            setForm(EMPTY_FORM);
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit "{editTarget?.name}"</DialogTitle>
            <DialogDescription>
              Type metadata controls how this category appears. Fields below control the
              <span className="font-medium"> structure of every devotional</span> in this type.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={onSaveEdit} className="space-y-4">
            <TypeForm form={form} setForm={setForm} />
            <DialogFooter className="!mt-2">
              <Button type="button" variant="outline" onClick={() => setEditTarget(null)}>
                Close
              </Button>
              <Button type="submit" disabled={saving} data-testid="type-edit-submit">
                {saving ? "Saving…" : "Save type details"}
              </Button>
            </DialogFooter>
          </form>

          {/* Field configuration — only visible when editing an existing type
              (need typeId for the API calls). */}
          {editTarget?.id && (
            <div className="pt-5 mt-5 border-t border-border">
              <FieldsManager typeId={editTarget.id} />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This deletes the type and its associated fields. Existing devotionals will keep their data but lose this label.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              data-testid="confirm-delete-type"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
